import { useSEO } from "../../hooks/useSEO";
import { seoConfig } from "../../utils/seoConfig";
import { useRef, useEffect, useState } from 'react';
import { ReactLenis } from '@studio-freight/react-lenis';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  ArrowRight,
  ChevronLeft,
  ChevronRight,
  Linkedin,
  Instagram,
  Facebook,
  Youtube,
  Twitter,
  Users,
  School,
  GraduationCap,
  Smartphone,
  FileText,
  Languages,
  PenTool,
  BarChart,
  Lock,
  Menu,
  X,
} from 'lucide-react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { WanderfulHero } from './WanderfulHero';

const ScrollingCardsSection = () => {
  const cardsSectionRef = useRef<HTMLDivElement>(null);
  const cardsRowRef = useRef<HTMLDivElement>(null);
  const leftCardRef = useRef<HTMLDivElement>(null);
  const centerCardRef = useRef<HTMLDivElement>(null);
  const rightCardRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!cardsSectionRef.current) return;
    if (window.innerWidth < 1024) return; // Desktop only (hide animation on iPad/mobile)

    gsap.registerPlugin(ScrollTrigger);

    const tl = gsap.timeline({
      scrollTrigger: {
        trigger: cardsSectionRef.current,
        start: "top top",
        end: "+=2500",
        scrub: 1.2,
        pin: true,
        anticipatePin: 1,
      }
    });

    tl.to(
      [leftCardRef.current, rightCardRef.current],
      { y: -500, opacity: 0, duration: 1, ease: "power2.inOut" },
      0
    );

    const targetScale = Math.min((window.innerWidth * 0.70) / 750, 1.5);

    tl.to(
      centerCardRef.current,
      {
        scale: targetScale,
        borderRadius: "28px",
        duration: 1.5,
        ease: "power2.inOut"
      },
      1
    );

    return () => {
      ScrollTrigger.getAll().forEach(t => t.kill());
    };
  }, []);

  return (
    <section id="video-section" ref={cardsSectionRef} className="relative min-h-screen md:h-screen bg-white overflow-visible md:overflow-hidden flex items-center justify-center py-10 md:py-0">
      <div
        ref={cardsRowRef}
        className="relative w-full max-w-6xl mx-auto px-4 md:px-8 flex flex-col md:flex-row justify-center items-center z-10 gap-6 md:gap-8"
      >
         {/* Card 1 (Left Video) */}
         <div
           ref={leftCardRef}
           className="relative w-full md:w-[280px] h-[250px] md:h-[450px] rounded-[32px] overflow-hidden group flex-shrink-0 bg-white"
         >
            <video autoPlay loop muted playsInline className="w-full h-full object-cover">
              <source src="/new/career-outcome.mp4" type="video/mp4" />
            </video>
            <div className="absolute inset-x-0 top-0 h-1/3 bg-gradient-to-b from-white to-transparent z-10" />
         </div>

         {/* Card 2 (Center Video) */}
         <div
           ref={centerCardRef}
           className="relative w-full md:w-[750px] h-[250px] md:h-[450px] overflow-hidden group flex-shrink-0 z-30 bg-white"
           style={{ borderRadius: "20px" }}
         >
            <video autoPlay loop muted playsInline className="w-full h-full object-cover">
              <source src="/WhatsApp Video 2026-03-12 at 9.22.47 AM.mp4" type="video/mp4" />
            </video>
         </div>

         {/* Card 3 (Right Video) */}
         <div
           ref={rightCardRef}
           className="relative w-full md:w-[280px] h-[250px] md:h-[450px] rounded-[32px] overflow-hidden group flex-shrink-0 bg-white"
         >
            <video autoPlay loop muted playsInline className="w-full h-full object-cover">
              <source src="/Screen Recording 2026-05-05 at 3.38.22 PM.mp4" type="video/mp4" />
            </video>
            <div className="absolute inset-x-0 top-0 h-1/3 bg-gradient-to-b from-white to-transparent z-10" />
         </div>
      </div>
    </section>
  );
};

// Partners Section
const PartnersSection = () => {
  const institutions = [
    { label: "Tier-1 University, MP", size: "12,000", type: "University", bg: "/university_bg.png" },
    { label: "Top CBSE School, Delhi", size: "5,000", type: "School", bg: "/school_bg.png" },
    { label: "Leading NEET Coaching, RJ", size: "25,000", type: "Coaching", bg: "/coaching_bg.png" },
    { label: "State University, MH", size: "40,000", type: "University", bg: "/state_university_bg.png" },
  ];

  return (
    <section className="py-8 sm:py-12 md:py-24 bg-white border-y border-gray-100">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <div className="text-center mb-6 sm:mb-8 md:mb-16">
          <span className="text-xs font-bold uppercase tracking-wider text-gray-500 bg-gray-100 px-4 py-1.5 rounded-full shadow-xl">
            Trusted by leading institutions
          </span>
          <h2 className="text-2xl sm:text-3xl font-bold text-black mt-6">
            Empowering Education at Scale
          </h2>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {institutions.map((inst, index) => (
            <div
              key={index}
              className="relative rounded-2xl p-4 sm:p-6 md:p-8 overflow-hidden hover:shadow-xl transition-all duration-300 group flex flex-col justify-between min-h-[250px] text-white"
            >
              {/* Background Image */}
              <div className="absolute inset-0 z-0">
                <img src={inst.bg} alt={inst.label} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700" />
                {/* Dark Overlay */}
                <div className="absolute inset-0 bg-gradient-to-b from-black/50 to-black/80" />
              </div>

              <div className="relative z-10 text-center flex-1 flex flex-col items-center justify-center pt-6">
                <div className="text-3xl sm:text-4xl md:text-5xl font-bold mb-1">
                  {inst.size}
                </div>
                <div className="text-xs font-bold uppercase text-gray-300 tracking-wider">
                  Students
                </div>
              </div>

              <div className="relative z-10 border-t border-white/20 pt-4 mt-auto text-center">
                <span className="text-xs font-bold uppercase text-white/60 tracking-wider">
                  {inst.type}
                </span>
                <h3 className="text-[14px] font-bold mt-1 leading-tight">
                  {inst.label}
                </h3>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

const AnimatedStat = ({ value, prefix = "", suffix = "", decimals = 0, useIndianFormat = false }: { value: number; prefix?: string; suffix?: string; decimals?: number; useIndianFormat?: boolean }) => {
  const elementRef = useRef<HTMLSpanElement>(null);

  useEffect(() => {
    if (!elementRef.current) return;

    gsap.registerPlugin(ScrollTrigger);

    const obj = { val: 0 };
    const formatter = new Intl.NumberFormat(useIndianFormat ? 'en-IN' : 'en-US', {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    });

    gsap.to(obj, {
      val: value,
      duration: 2,
      ease: 'power2.out',
      onUpdate: () => {
        if (elementRef.current) {
          elementRef.current.innerText = `${prefix}${formatter.format(obj.val)}${suffix}`;
        }
      },
      scrollTrigger: {
        trigger: elementRef.current,
        start: 'top 85%',
        toggleActions: 'play none none none',
      }
    });
  }, [value, prefix, suffix, decimals, useIndianFormat]);

  return <span ref={elementRef} className="text-3xl md:text-4xl font-bold text-slate-900 mb-2">{prefix}0{suffix}</span>;
};

const StatsStripSection = () => {
  const stats = [
    { value: 200000, prefix: "", suffix: "+", decimals: 0, useIndianFormat: true, label: "Questions evaluated in pilots" },
    { value: 95, prefix: "", suffix: "%+", decimals: 0, label: "Accuracy vs human evaluators" },
    { value: 0.40, prefix: "₹", suffix: "", decimals: 2, label: "Per page starting price" },
    { value: 48, prefix: "", suffix: " hours", decimals: 0, label: "From signup to first evaluation" },
    { value: 100, prefix: "", suffix: "%", decimals: 0, label: "Client retention rate" },
  ];

  return (
    <section className="py-8 sm:py-12 md:py-16 bg-gray-50 border-y border-gray-100">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-4 sm:gap-6 md:gap-8 items-center text-center">
          {stats.map((stat, index) => (
            <div key={index} className={`flex flex-col items-center ${index === 4 ? 'col-span-2 sm:col-span-3 md:col-span-1' : ''}`}>
              <div className="text-2xl sm:text-3xl md:text-4xl font-bold mb-2">
                <AnimatedStat
                  value={stat.value}
                  prefix={stat.prefix}
                  suffix={stat.suffix}
                  decimals={stat.decimals}
                  useIndianFormat={stat.useIndianFormat}
                />
              </div>
              <span className="text-xs sm:text-sm text-slate-600 font-medium">{stat.label}</span>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// The Problem Section
const ProblemSection = () => {
  const [activeIndex, setActiveIndex] = useState(0);
  
  const problems = [
    { 
      number: "01",
      title: "Time Drain", 
      desc: "Teachers spend 30% of their week marking answer sheets manually, leaving less time for actual teaching and student support.",
      image: "/stressed_teacher.png"
    },
    { 
      number: "02",
      title: "Delayed Feedback", 
      desc: "Results take 2–3 weeks to reach students. This delay slows down the learning loop and makes it harder to address gaps effectively.",
      image: "/Results_take_2–3_weeks_Delayed_202605112024.jpeg"
    },
    { 
      number: "03",
      title: "Two evaluators, two different marks", 
      desc: "Subjective bias leads to inconsistent scoring for the same paper. Subjective bias leads to unfair and inconsistent scoring.",
      image: "/Two_evaluators,_two_different_marks_202605112026.jpeg"
    },
  ];

  const handleNext = () => {
    setActiveIndex((prev) => (prev + 1) % problems.length);
  };

  const handlePrev = () => {
    setActiveIndex((prev) => (prev - 1 + problems.length) % problems.length);
  };

  useEffect(() => {
    const interval = setInterval(() => {
      setActiveIndex((prev) => (prev + 1) % problems.length);
    }, 4000); // 4 seconds

    return () => clearInterval(interval);
  }, [problems.length]);

  return (
    <section className="py-8 sm:py-12 md:py-24 bg-white px-4 sm:px-6">
      <div className="max-w-7xl mx-auto">
        <div className="bg-[#f9f9f9] rounded-[24px] sm:rounded-[40px] p-4 sm:p-6 md:p-8 lg:p-16">
          <div className="text-center mb-6 sm:mb-8 md:mb-12">
            <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-black mb-4">The Problem with Manual Marking</h2>
            <p className="text-gray-600 max-w-2xl mx-auto font-medium text-sm sm:text-base">Traditional evaluation is slow, inconsistent, and drains teacher energy.</p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-3 sm:gap-4 md:gap-6 lg:gap-8 items-center">
            {/* Left Card */}
            <div className="lg:col-span-4 bg-white rounded-[32px] h-[280px] sm:h-[320px] md:h-[350px] shadow-sm border border-gray-50 relative overflow-hidden">
              <AnimatePresence mode="wait">
                <motion.div
                  key={activeIndex}
                  initial={{ opacity: 0, x: 30 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -30 }}
                  transition={{ duration: 0.6, ease: [0.4, 0, 0.2, 1] }}
                  className="absolute inset-0 p-4 sm:p-5 md:p-8 lg:p-10 flex flex-col justify-between"
                >
                  <div>
                    <div className="text-4xl sm:text-5xl md:text-6xl font-bold text-[#e5e7eb] mb-4 sm:mb-6 md:mb-8">
                      {problems[activeIndex].number}
                    </div>
                    <h3 className="text-lg sm:text-xl md:text-2xl font-bold text-black mb-3 md:mb-4">
                      {problems[activeIndex].title}
                    </h3>
                    <p className="text-gray-600 font-medium text-xs sm:text-sm md:text-base">
                      {problems[activeIndex].desc}
                    </p>
                  </div>
                </motion.div>
              </AnimatePresence>
            </div>

            {/* Right Image */}
            <div className="lg:col-span-8 relative h-[280px] sm:h-[320px] md:h-[350px] rounded-[32px] overflow-hidden">
              <AnimatePresence mode="wait">
                <motion.img
                  key={activeIndex}
                  src={problems[activeIndex].image}
                  alt={problems[activeIndex].title}
                  initial={{ opacity: 0, scale: 1.05 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.95 }}
                  transition={{ duration: 0.6, ease: [0.4, 0, 0.2, 1] }}
                  className="absolute inset-0 w-full h-full object-cover"
                />
              </AnimatePresence>
            </div>
          </div>
          
          {/* Controls */}
          <div className="flex justify-center items-center mt-8">
            {/* Arrows */}
            <div className="flex justify-center gap-4">
              <button 
                onClick={handlePrev}
                className="w-12 h-12 rounded-full shadow-xl flex items-center justify-center bg-white hover:bg-gray-50 transition-colors shadow-sm"
              >
                <ChevronLeft className="w-5 h-5 text-gray-600" />
              </button>
              <button 
                onClick={handleNext}
                className="w-12 h-12 rounded-full shadow-xl flex items-center justify-center bg-white hover:bg-gray-50 transition-colors shadow-sm"
              >
                <ChevronRight className="w-5 h-5 text-gray-600" />
              </button>
            </div>
            
          </div>
        </div>
      </div>
    </section>
  );
};

const ApproachSection = () => {
  const steps = [
    { 
      number: "01", 
      title: "Upload", 
      desc: "Upload handwritten answer sheets via smartphone or scanner.", 
      image: "/WhatsApp Video 2026-03-12 at 9.22.47 AM.mp4",
      isVideo: true 
    },
    {
      number: "02",
      title: "Configure",
      desc: "Define evaluation criteria or use standard rubrics.",
      image: "/new/configure.png"
    },
    {
      number: "03",
      title: "Evaluate",
      desc: "Parakh AI analyzes and scores the papers with 95% accuracy.",
      image: "/new/evaluate.png"
    },
    {
      number: "04",
      title: "Review",
      desc: "Teachers review the scores and detailed feedback.",
      image: "/new/review.png"
    },
  ];

  const sectionRef = useRef<HTMLDivElement>(null);
  const cardsContainerRef = useRef<HTMLDivElement>(null);
  const cardsRef = useRef<(HTMLDivElement | null)[]>([]);

  useEffect(() => {
    if (!sectionRef.current || !cardsContainerRef.current) return;

    const isDesktop = window.innerWidth >= 1024;

    if (!isDesktop) {
      // Reset all cards to visible on iPad/mobile - remove all GSAP inline styles
      const cards = cardsRef.current.filter(Boolean);
      cards.forEach((card) => {
        if (card) {
          gsap.set(card, {
            clearProps: "all"
          });
          // Force display block to ensure cards stack vertically
          card.style.display = "grid";
          card.style.position = "relative";
        }
      });
      return;
    }

    gsap.registerPlugin(ScrollTrigger);

    const cards = cardsRef.current.filter(Boolean);

    const tl = gsap.timeline({
      scrollTrigger: {
        trigger: sectionRef.current,
        start: "top top",
        end: `+=${cards.length * 1500}`, // Scroll distance
        scrub: 1,
        pin: true,
        anticipatePin: 1,
      }
    });

    cards.forEach((card, index) => {
      // Set initial state
      gsap.set(card, {
        scale: 0.1,
        x: 600,
        y: 400,
        opacity: 0,
        transformOrigin: "bottom right"
      });

      // Animate in
      tl.to(card, {
        scale: 1,
        x: 0,
        y: 0,
        opacity: 1,
        duration: 1,
        ease: "power2.out"
      }, index * 2);

      // Animate out (if not the last one)
      if (index < cards.length - 1) {
        tl.to(card, {
          x: -600,
          opacity: 0,
          scale: 0.5,
          duration: 1,
          ease: "power2.in"
        }, (index * 2) + 1.5);
      }
    });

    return () => {
      ScrollTrigger.getAll().forEach(t => t.kill());
    };
  }, []);

  return (
    <section id="how-it-works" ref={sectionRef} className="py-8 sm:py-12 md:py-24 bg-gray-50 relative min-h-screen flex flex-col justify-center">
      <div className="w-full px-4 sm:px-6">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="text-center mb-8 sm:mb-12 md:mb-20">
            <span className="text-sm font-bold text-slate-900 uppercase tracking-widest bg-gray-100 px-4 py-1.5 rounded-full">Process</span>
            <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-black mt-4">How it works</h2>
            <p className="text-gray-600 max-w-2xl mx-auto mt-2 font-medium text-sm sm:text-base">A seamless 4-step workflow for automated evaluation.</p>
          </div>

          {/* Cards Container */}
          <div ref={cardsContainerRef} className="relative w-full h-auto flex flex-col gap-4 sm:gap-6 lg:gap-0 lg:h-[350px]">
            {steps.map((step, index) => (
              <div
                key={index}
                ref={el => cardsRef.current[index] = el}
                className="relative lg:absolute lg:inset-0 grid grid-cols-1 lg:grid-cols-12 gap-3 sm:gap-4 lg:gap-8 items-center w-full"
              >
                {/* Left Card */}
                <div className="lg:col-span-4 bg-white rounded-[32px] h-auto lg:h-[350px] lg:min-h-[350px] shadow-sm border border-gray-50 relative overflow-hidden p-4 sm:p-5 md:p-6 lg:p-10 flex flex-col justify-between">
                  <div>
                    <div className="text-4xl sm:text-5xl md:text-6xl font-bold text-[#e5e7eb] mb-3 sm:mb-4 md:mb-6">
                      {step.number}
                    </div>
                    <h3 className="text-lg sm:text-xl md:text-2xl lg:text-3xl font-bold text-black mb-2 sm:mb-3 md:mb-4">{step.title}</h3>
                    <p className="text-gray-600 font-medium text-xs sm:text-sm md:text-base">{step.desc}</p>
                  </div>
                </div>

                {/* Right Image */}
                <div className="lg:col-span-8 relative h-[180px] sm:h-[240px] lg:h-[350px] rounded-[32px] overflow-hidden">
                  {step.isVideo ? (
                    <video autoPlay loop muted playsInline className="w-full h-full object-cover">
                      <source src={step.image} type="video/mp4" />
                    </video>
                  ) : (
                    <img
                      src={step.image}
                      alt={step.title}
                      className="w-full h-full rounded-2xl shadow-[0_20px_50px_rgba(0,0,0,0.15)] object-cover"
                    />
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

const SolutionsSection = () => {
  const audiences = [
    {
      title: "Universities",
      description: "Scale evaluation for semester exams. Maintain high standards and audit trails for compliance.",
      icon: <GraduationCap className="w-8 h-8" />,
      features: ["Scalable for large batches", "Strict compliance & audit", "Integration with LMS"],
      bgImage: "/university_bg.png"
    },
    {
      title: "Coachings",
      description: "Fast-track evaluation for test series and mock exams. Handle high volumes with instant results.",
      icon: <Users className="w-8 h-8" />,
      features: ["Instant result generation", "Bulk upload processing", "Detailed error analysis"],
      bgImage: "/coaching_bg.png"
    },
    {
      title: "Schools",
      description: "Reduce teacher workload for unit tests and term exams. Ensure unbiased and consistent marking.",
      icon: <School className="w-8 h-8" />,
      features: ["Unbiased marking", "Teacher workload reduction", "Parent-teacher reports"],
      bgImage: "/school_bg.png"
    }
  ];

  return (
    <section className="py-8 sm:py-12 md:py-24 bg-white relative overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <div className="bg-[#f9f9f9] rounded-[24px] sm:rounded-[40px] p-4 sm:p-6 md:p-8 lg:p-16">
          <div className="text-center mb-6 sm:mb-8 md:mb-16">
            <span className="text-sm font-bold text-slate-900 uppercase tracking-widest bg-gray-100 px-4 py-1.5 rounded-full">Audiences</span>
            <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-black mt-4">Solutions by Audience</h2>
            <p className="text-gray-600 max-w-2xl mx-auto mt-2 font-medium text-sm sm:text-base">Tailored evaluation workflows for every level of education.</p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 md:gap-8">
            {audiences.map((aud, index) => (
              <div
                key={index}
                className="bg-white rounded-3xl overflow-hidden border border-gray-100 shadow-[0_10px_30px_rgba(0,0,0,0.03)] hover:shadow-[0_20px_50px_rgba(0,0,0,0.06)] hover:border-gray-200 transition-all duration-500 flex flex-col group"
              >
                {/* Image Area */}
                <div className="h-40 sm:h-48 md:h-52 relative overflow-hidden">
                  <div
                    className="absolute inset-0 bg-cover bg-center transition-transform duration-700 group-hover:scale-110"
                    style={{ backgroundImage: `url('${aud.bgImage}')` }}
                  />
                  <div className="absolute inset-0 bg-black/10" />
                </div>

                {/* Content Area */}
                <div className="p-4 sm:p-6 md:p-8 flex flex-col flex-grow relative">
                  {/* Overlapping Icon */}
                  <div className="absolute -top-6 left-6 sm:left-8 w-12 h-12 sm:w-14 sm:h-14 rounded-2xl bg-white shadow-lg flex items-center justify-center text-black group-hover:bg-black group-hover:text-white transition-colors duration-500">
                    {aud.icon}
                  </div>

                  <div className="mt-4 sm:mt-6">
                    <h3 className="text-xl sm:text-2xl font-bold text-slate-900 mb-2 sm:mb-3">{aud.title}</h3>
                    <p className="text-slate-600 text-xs sm:text-sm leading-relaxed mb-4 sm:mb-6">{aud.description}</p>

                    <div className="w-full h-px bg-gray-100 mb-4 sm:mb-6" />
                    <ul className="space-y-2 sm:space-y-3">
                      {aud.features.map((feat, i) => (
                        <li key={i} className="flex items-center gap-2 text-xs sm:text-sm text-slate-600">
                          <div className="w-1.5 h-1.5 rounded-full bg-black flex-shrink-0" />
                          {feat}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

const FeaturesDeepDiveSection = () => {
  const [activeTab, setActiveTab] = useState(0);

  const features = [
    { 
      title: "Smartphone Scanning", 
      desc: "No heavy scanners needed. Teachers or students can scan answer sheets directly using any smartphone camera. Our AI corrects perspective and lighting automatically.", 
      icon: <Smartphone className="w-6 h-6" />,
      detail: "Supports all standard smartphone cameras. Auto-cropping and perspective correction ensure high accuracy even in poor lighting."
    },
    { 
      title: "Custom Rubrics", 
      desc: "Upload your own marking schemes. The AI adapts to your specific grading standards.", 
      icon: <FileText className="w-6 h-6" />,
      detail: "Define step-by-step marking, partial marking rules, and specific keywords to look for. Highly customizable for any board or exam."
    },
    { 
      title: "Multilingual OCR", 
      desc: "Reads mixed language handwriting (Hindi + English) with high accuracy.", 
      icon: <Languages className="w-6 h-6" />,
      detail: "Trained on millions of Indian handwriting samples. Seamlessly handles Hindi, English, and mixed (Hinglish) scripts."
    },
    { 
      title: "Smart Detection", 
      desc: "Identifies diagrams, graphs, and strikethroughs to evaluate only valid answers.", 
      icon: <PenTool className="w-6 h-6" />,
      detail: "Advanced computer vision detects diagrams and ignores cancelled text or rough work automatically."
    },
    { 
      title: "Rich Analytics", 
      desc: "Download detailed analytical reports in Excel or PDF formats.", 
      icon: <BarChart className="w-6 h-6" />,
      detail: "Get student-wise, question-wise, and topic-wise analytics to identify learning gaps."
    },
    { 
      title: "Bank-Grade Security", 
      desc: "End-to-end encryption and compliance with global standards.", 
      icon: <Lock className="w-6 h-6" />,
      detail: "AES-256 encryption for data at rest and in transit. GDPR and ISO 27001 compliant infrastructure."
    }
  ];

  useEffect(() => {
    const interval = setInterval(() => {
      setActiveTab((prev) => (prev + 1) % features.length);
    }, 2000); // 2 seconds

    return () => clearInterval(interval);
  }, [features.length]);

  return (
    <section className="py-8 sm:py-12 md:py-24 bg-gray-50 relative overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <div className="text-center mb-6 sm:mb-8 md:mb-16">
          <span className="text-sm font-bold text-slate-900 uppercase tracking-widest bg-gray-100 px-4 py-1.5 rounded-full">Features</span>
          <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-black mt-4">Features Deep-Dive</h2>
          <p className="text-gray-600 max-w-2xl mx-auto mt-2 font-medium text-sm sm:text-base">Click on a feature to explore its capabilities.</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 sm:gap-8 md:gap-12 items-start">

          {/* Left Side: Tabs List */}
          <div className="lg:col-span-4 space-y-2 sm:space-y-3">
            {features.map((feat, index) => (
              <button
                key={index}
                onClick={() => setActiveTab(index)}
                className={`w-full flex items-center gap-2 sm:gap-3 py-2 sm:py-3 px-3 sm:px-4 rounded-xl transition-all duration-300 text-left ${
                  activeTab === index
                    ? 'bg-black text-white shadow-2xl'
                    : 'bg-white text-slate-900 border border-gray-100 hover:border-gray-300 hover:shadow-sm'
                }`}
              >
                <div className={`w-8 sm:w-10 h-8 sm:h-10 rounded-xl flex items-center justify-center transition-colors flex-shrink-0 ${
                  activeTab === index ? 'bg-white/10 text-white' : 'bg-gray-100 text-black'
                }`}>
                  {feat.icon}
                </div>
                <div>
                  <h3 className="font-bold text-xs sm:text-sm">{feat.title}</h3>
                </div>
              </button>
            ))}
          </div>

          {/* Right Side: Active Feature Detail */}
          <div className="lg:col-span-8 bg-white p-4 sm:p-6 md:p-8 rounded-3xl border border-gray-100 shadow-[0_10px_30px_rgba(0,0,0,0.02)] flex flex-col justify-start">
            <AnimatePresence mode="wait">
              <motion.div
                key={activeTab}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                transition={{ duration: 0.3 }}
                className="flex flex-col space-y-4 sm:space-y-6"
              >
                <div className="w-12 sm:w-16 h-12 sm:h-16 rounded-2xl bg-gray-50 flex items-center justify-center text-black mb-4 sm:mb-8">
                  {features[activeTab].icon}
                </div>

                <h3 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-2 sm:mb-4">{features[activeTab].title}</h3>
                <p className="text-slate-600 text-sm sm:text-base lg:text-lg leading-relaxed mb-4 sm:mb-6">{features[activeTab].desc}</p>

                <div className="mt-auto bg-gray-50 p-4 sm:p-6 rounded-2xl border border-gray-100">
                  <h4 className="text-xs font-bold uppercase tracking-widest text-slate-900 mb-2">How it works</h4>
                  <p className="text-slate-600 text-xs sm:text-sm">{features[activeTab].detail}</p>
                </div>
              </motion.div>
            </AnimatePresence>
          </div>

        </div>
      </div>
    </section>
  );
};

// Why Parakh is Different Section
const ComparisonSection = () => {
  const rows = [
    { feature: "Speed", manual: "2-3 weeks", generic: "Instant", parakh: "Minutes" },
    { feature: "Accuracy", manual: "Subjective", generic: "Variable (60-70%)", parakh: "95%+" },
    { feature: "Handwriting Support", manual: "Yes", generic: "Poor", parakh: "Excellent (Hindi + English)" },
    { feature: "Diagram Handling", manual: "Yes", generic: "No", parakh: "Yes (with correction labels)" },
    { feature: "Cost", manual: "High (Teacher time)", generic: "Low", parakh: "₹0.40 per page" },
  ];

  return (
    <section className="py-8 sm:py-12 md:py-24 bg-white relative overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        {/* Contained Box wrapper for consistency */}
        <div className="bg-[#f9f9f9] rounded-[24px] sm:rounded-[40px] p-4 sm:p-6 md:p-8 lg:p-16">
          <div className="text-center mb-6 sm:mb-8 md:mb-12">
            <span className="text-sm font-bold text-slate-900 uppercase tracking-widest bg-gray-100 px-4 py-1.5 rounded-full">Comparison</span>
            <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-black mt-4 mb-4">Why Parakh is Different</h2>
            <p className="text-gray-600 max-w-2xl mx-auto font-medium text-sm sm:text-base">How we compare against manual marking and generic AI tools.</p>
          </div>

          {/* Mobile Cards View */}
          <div className="block md:hidden space-y-3 sm:space-y-4">
            {rows.map((row, index) => (
              <div key={index} className="bg-white p-4 sm:p-6 rounded-2xl border border-gray-100 shadow-sm">
                <h3 className="font-bold text-base sm:text-lg text-black mb-3">{row.feature}</h3>

                <div className="grid grid-cols-2 gap-3 sm:gap-4 text-xs sm:text-sm">
                  <div>
                    <span className="text-gray-500 block mb-1">Manual Marking</span>
                    <span className="text-gray-900 font-medium">{row.manual}</span>
                  </div>
                  <div>
                    <span className="text-gray-500 block mb-1">Generic AI</span>
                    <span className="text-gray-900 font-medium">{row.generic}</span>
                  </div>
                </div>

                <div className="mt-3 pt-3 border-t border-gray-100">
                  <span className="text-gray-500 block mb-1 text-xs sm:text-sm">Parakh AI</span>
                  <span className="text-slate-900 font-bold flex items-center gap-1 text-sm sm:text-base">
                    <svg className="w-4 h-4 text-slate-900" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                    </svg>
                    {row.parakh}
                  </span>
                </div>
              </div>
            ))}
          </div>

          {/* Desktop Table View */}
          <div className="hidden md:block overflow-x-auto">
            <table className="w-full text-left border-collapse bg-white rounded-3xl overflow-hidden shadow-sm border border-gray-100">
              <thead>
                <tr className="bg-black text-white">
                  <th className="p-4 sm:p-6 font-bold uppercase text-xs sm:text-sm tracking-wider">Feature</th>
                  <th className="p-4 sm:p-6 font-bold uppercase text-xs sm:text-sm tracking-wider">Manual Marking</th>
                  <th className="p-4 sm:p-6 font-bold uppercase text-xs sm:text-sm tracking-wider">Generic AI</th>
                  <th className="p-4 sm:p-6 font-bold uppercase text-xs sm:text-sm tracking-wider bg-slate-900 relative">
                    Parakh AI
                  </th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row, index) => (
                  <tr key={index} className="border-b border-gray-50 hover:bg-gray-50/50 transition-colors">
                    <td className="p-4 sm:p-6 font-bold text-gray-900 text-sm sm:text-base">{row.feature}</td>
                    <td className="p-4 sm:p-6 text-gray-600 font-medium text-sm sm:text-base">{row.manual}</td>
                    <td className="p-4 sm:p-6 text-gray-600 font-medium text-sm sm:text-base">
                      {row.generic === "Poor" || row.generic === "No" ? (
                        <div className="flex items-center gap-2 text-gray-500">
                          <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                          </svg>
                          <span>{row.generic}</span>
                        </div>
                      ) : (
                        <span>{row.generic}</span>
                      )}
                    </td>
                    <td className="p-4 sm:p-6 text-slate-900 font-bold bg-gray-50/50 relative text-sm sm:text-base">
                      <div className="flex items-center gap-2">
                        <svg className="w-4 h-4 flex-shrink-0 text-slate-900" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                        </svg>
                        <span>{row.parakh}</span>
                      </div>

                      {/* Highlighted column border effects (Gray instead of Blue) */}
                      <div className="absolute inset-y-0 right-0 w-px bg-gray-200" />
                      <div className="absolute inset-y-0 left-0 w-px bg-gray-200" />
                      {index === rows.length - 1 && (
                        <div className="absolute inset-x-0 bottom-0 h-px bg-gray-200" />
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </section>
  );
};

export const Navbar = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isScrolled, setIsScrolled] = useState(false);
  const [navTheme, setNavTheme] = useState('default');

  const isActive = (path: string) => {
    if (path === '/') return location.pathname === '/';
    return location.pathname.startsWith(path);
  };

  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 50) {
        setIsScrolled(true);
      } else {
        setIsScrolled(false);
      }
    };

    const handleThemeChange = (e: any) => {
      setNavTheme(e.detail);
    };

    window.addEventListener('scroll', handleScroll);
    window.addEventListener('nav-theme' as any, handleThemeChange);
    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('nav-theme' as any, handleThemeChange);
    };
  }, []);

  const handleGetStarted = () => {
    navigate('/login');
  };

  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isUseCasesOpen, setIsUseCasesOpen] = useState(false);
  const [isSolutionsOpen, setIsSolutionsOpen] = useState(false);
  const [isCompanyOpen, setIsCompanyOpen] = useState(false);

  const isNavbarTransparent = navTheme === 'transparent' || !isScrolled;

  return (
    <nav className="fixed top-2 sm:top-4 left-0 right-0 z-50 flex flex-col items-center justify-center px-2 sm:px-4 md:px-6">
      <div className={`rounded-full border transition-all duration-300 px-3 py-1.5 sm:px-4 sm:py-2 md:px-6 md:py-3 flex items-center justify-between w-full max-w-7xl ${isNavbarTransparent ? 'bg-transparent border-transparent' : 'bg-white border-gray-200 shadow-sm'}`}>
        {/* Logo & Badge */}
        <div className="flex items-center gap-2 sm:gap-3">
          <a href="/" className={`flex items-center gap-2 sm:gap-3 group shrink-0 ${isNavbarTransparent ? 'text-white' : 'text-black'}`}>
            <div className={`p-1 sm:p-1.5 rounded-lg sm:rounded-2xl transition-all duration-500 ${
              isNavbarTransparent
                ? 'bg-white/5 border border-white/10 hover:border-white/20 shadow-[0_0_20px_rgba(255,255,255,0.05)]'
                : 'bg-white border border-gray-100 shadow-[0_4px_12px_rgba(0,0,0,0.03)]'
            } group-hover:scale-105 group-hover:rotate-[-2deg]`}>
              <img
                src="/new/my_wesbite_name_is_Parakh_202605191852.jpeg"
                alt="Parakh AI Logo"
                className="w-6 sm:w-9 h-6 sm:h-9 rounded-lg sm:rounded-xl object-contain"
              />
            </div>
            <span className="font-sans font-black tracking-[-0.03em] text-xs sm:text-lg md:text-xl transition-all duration-300 hidden sm:inline">
              <span className={isNavbarTransparent ? 'text-white group-hover:text-white/90' : 'text-slate-900'}>Parakh</span>
              <span className={`ml-1 transition-colors duration-300 ${isNavbarTransparent ? 'text-white/60' : 'text-slate-500'}`}>AI</span>
            </span>
          </a>
        </div>
        
        {/* Nav Links */}
        <div className={`hidden lg:flex items-center gap-4 xl:gap-6 text-[11px] lg:text-[13px] font-bold tracking-wide uppercase ${isNavbarTransparent ? 'text-white' : 'text-gray-900'}`}>
          <a href="/" className={`transition-colors ${isActive('/') ? (isNavbarTransparent ? 'text-white/100 border-b-2 border-white' : 'text-black border-b-2 border-black') : 'hover:opacity-70'}`}>Home</a>
          <div className={`group relative flex items-center gap-1 cursor-pointer transition-colors ${isActive('/use-cases') ? (isNavbarTransparent ? 'text-white/100 border-b-2 border-white' : 'text-black border-b-2 border-black') : ''}`}>
            <span className={`transition-opacity ${isActive('/use-cases') ? '' : 'group-hover:opacity-70'}`}>Use Cases ▾</span>
            {/* Dropdown */}
            <div className="absolute top-full left-0 mt-2 w-64 border-0 rounded-xl invisible group-hover:visible transition-all duration-200 opacity-0 group-hover:opacity-100 z-50 normal-case font-medium bg-black text-white shadow-2xl">
              <div className="py-2">
                <a href="/use-cases/upsc" className="block px-4 py-2.5 transition-colors hover:bg-black hover:text-white text-white">UPSC Mains Evaluation</a>
                <a href="/use-cases/jee-neet" className="block px-4 py-2.5 transition-colors hover:bg-black hover:text-white text-white">JEE/NEET Mock Test Checking</a>
                <a href="/use-cases/cbse" className="block px-4 py-2.5 transition-colors hover:bg-black hover:text-white text-white">CBSE Board Practice Papers</a>
                <a href="/use-cases/university" className="block px-4 py-2.5 transition-colors hover:bg-black hover:text-white text-white">University Internal Assessments</a>
                <a href="/use-cases/ca-cs" className="block px-4 py-2.5 transition-colors hover:bg-black hover:text-white text-white">CA/CS/CMA Mock Evaluation</a>
              </div>
            </div>
          </div>
          <div className={`group relative flex items-center gap-1 cursor-pointer transition-colors ${isActive('/coaching') || isActive('/schools') || isActive('/universities') ? (isNavbarTransparent ? 'text-white/100 border-b-2 border-white' : 'text-black border-b-2 border-black') : ''}`}>
            <span className={`transition-opacity ${isActive('/coaching') || isActive('/schools') || isActive('/universities') ? '' : 'group-hover:opacity-70'}`}>Solutions ▾</span>
            {/* Cascaded Dropdown */}
            <div className="absolute top-full left-0 mt-2 w-56 border-0 rounded-xl invisible group-hover:visible transition-all duration-200 opacity-0 group-hover:opacity-100 z-50 normal-case font-medium bg-black text-white shadow-2xl">
              <div className="py-2">
                <a href="/coaching" className="block px-4 py-2.5 transition-colors hover:bg-black hover:text-white text-white">For Coaching Institutes</a>
                <a href="/schools" className="block px-4 py-2.5 transition-colors hover:bg-black hover:text-white text-white">For Schools</a>
                <a href="/universities" className="block px-4 py-2.5 transition-colors hover:bg-black hover:text-white text-white">For Universities</a>
              </div>
            </div>
          </div>
          <div className={`group relative flex items-center gap-1 cursor-pointer transition-colors ${isActive('/company') ? (isNavbarTransparent ? 'text-white/100 border-b-2 border-white' : 'text-black border-b-2 border-black') : ''}`}>
            <span className={`transition-opacity ${isActive('/company') ? '' : 'group-hover:opacity-70'}`}>Company ▾</span>
            {/* Company Dropdown */}
            <div className="absolute top-full left-0 mt-2 w-64 border-0 rounded-xl invisible group-hover:visible transition-all duration-200 opacity-0 group-hover:opacity-100 z-50 normal-case font-medium bg-black text-white shadow-2xl">
              <div className="py-2">
                {/* <a href="/company" className="block px-4 py-2 transition-colors hover:bg-white/10 hover:text-white font-bold text-xs uppercase tracking-widest border-b border-white/10 mb-1">Overview</a> */}
                <a href="/company/about" className="block px-4 py-2.5 transition-colors hover:bg-black hover:text-white text-white">About Kuberya AI</a>
                {/* <a href="/company/team" className="block px-4 py-2 transition-colors hover:bg-white/10 hover:text-white">Built by IIT Alumni</a> */}
                <a href="/company/careers" className="block px-4 py-2.5 transition-colors hover:bg-black hover:text-white text-white">Careers</a>
                {/* <a href="/company/press" className="block px-4 py-2 transition-colors hover:bg-white/10 hover:text-white font-bold text-xs uppercase tracking-widest border-b border-white/10 mb-1">Press & Mentions</a> */}
                <a href="/company/trust" className="block px-4 py-2.5 transition-colors hover:bg-black hover:text-white text-white">Trust Center</a>
              </div>
            </div>
          </div>
          <div className={`group relative flex items-center gap-1 cursor-pointer transition-colors ${isActive('/resources') ? (isNavbarTransparent ? 'text-white/100 border-b-2 border-white' : 'text-black border-b-2 border-black') : ''}`}>
            <span className={`transition-opacity ${isActive('/resources') ? '' : 'group-hover:opacity-70'}`}>Resources ▾</span>
            {/* Resources Dropdown */}
            <div className="absolute top-full left-0 mt-2 w-64 border-0 rounded-xl invisible group-hover:visible transition-all duration-200 opacity-0 group-hover:opacity-100 z-50 normal-case font-medium bg-black text-white shadow-2xl">
              <div className="py-2">
                {/* <a href="/resources" className="block px-4 py-2.5 transition-colors hover:bg-black hover:text-white font-bold text-xs uppercase tracking-widest border-b border-gray-700 mb-1 text-gray-300">View All Resources</a> */}
                <a href="/resources/case-study" className="block px-4 py-2.5 transition-colors hover:bg-black hover:text-white text-white">Anonymous Case Study</a>
                {/* <a href="/resources/blog" className="block px-4 py-2.5 transition-colors hover:bg-black hover:text-white text-white">Blog (SEO Hub)</a> */}
                {/* <a href="/resources/sample-reports" className="block px-4 py-2.5 transition-colors hover:bg-black hover:text-white text-white">Sample Evaluated Reports</a> */}
                <a href="/resources/rubric-templates" className="block px-4 py-2.5 transition-colors hover:bg-black hover:text-white text-white">Free Rubric Templates</a>
              </div>
            </div>
          </div>
          <a href="/support" className={`transition-colors ${isActive('/support') ? (isNavbarTransparent ? 'text-white/100 border-b-2 border-white' : 'text-black border-b-2 border-black') : 'hover:opacity-70'}`}>Support</a>
        </div>

        {/* Hamburger Menu Button */}
        <button
          className={`lg:hidden p-1.5 sm:p-2 rounded-full hover:bg-white/10 transition-colors ${isNavbarTransparent ? 'text-white' : 'text-black'}`}
          onClick={() => setIsMenuOpen(!isMenuOpen)}
        >
          {isMenuOpen ? <X className="w-4 sm:w-5 h-4 sm:h-5" /> : <Menu className="w-4 sm:w-5 h-4 sm:h-5" />}
        </button>

        {/* CTA */}
        <div className="hidden lg:flex items-center gap-2 xl:gap-4">
          <button
            onClick={handleGetStarted}
            className={`flex items-center gap-1.5 xl:gap-3 pl-3 xl:pl-5 pr-1 xl:pr-1.5 py-1 xl:py-1.5 rounded-full transition-colors group shrink-0 font-bold text-[10px] xl:text-[12px] tracking-wider uppercase whitespace-nowrap ${isNavbarTransparent ? 'bg-white text-black hover:bg-gray-100' : 'bg-black text-white hover:bg-gray-800'}`}
          >
             Try Now
             <div className={`rounded-full p-1 xl:p-1.5 group-hover:translate-x-1 transition-transform ${isNavbarTransparent ? 'bg-black text-white' : 'bg-white text-black'}`}>
                <ArrowRight className="w-3 xl:w-4 h-3 xl:h-4" />
             </div>
          </button>
        </div>
      </div>

      {/* Mobile Menu Dropdown */}
      {isMenuOpen && (
        <div className="lg:hidden w-full max-w-7xl mt-2 bg-black backdrop-blur-0 rounded-xl sm:rounded-2xl shadow-2xl border border-gray-700 p-4 sm:p-6 z-50 text-white text-sm">
          <div className="flex flex-col gap-3 sm:gap-4">
            <a href="/" className="text-base sm:text-lg font-bold hover:text-white/70 transition-colors">Home</a>

            <div className="border-t border-white/10 pt-3 sm:pt-4">
              <button
                onClick={() => setIsUseCasesOpen(!isUseCasesOpen)}
                className="flex items-center justify-between w-full text-xs sm:text-sm font-bold uppercase tracking-wider text-white mb-2"
              >
                Use Cases
                <span>{isUseCasesOpen ? '▴' : '▾'}</span>
              </button>
              {isUseCasesOpen && (
                <div className="flex flex-col gap-2 pl-2 mt-2 text-xs sm:text-sm">
                  <a href="/use-cases/upsc" className="hover:text-white/70 transition-colors">UPSC Evaluation</a>
                  <a href="/use-cases/jee-neet" className="hover:text-white/70 transition-colors">JEE/NEET Mock</a>
                  <a href="/use-cases/cbse" className="hover:text-white/70 transition-colors">CBSE Papers</a>
                  <a href="/use-cases/university" className="hover:text-white/70 transition-colors">University Assessments</a>
                  <a href="/use-cases/ca-cs" className="hover:text-white/70 transition-colors">CA/CS Evaluation</a>
                </div>
              )}
            </div>

            <div className="border-t border-white/10 pt-3 sm:pt-4">
              <button
                onClick={() => setIsSolutionsOpen(!isSolutionsOpen)}
                className="flex items-center justify-between w-full text-xs sm:text-sm font-bold uppercase tracking-wider text-white mb-2"
              >
                Solutions
                <span>{isSolutionsOpen ? '▴' : '▾'}</span>
              </button>
              {isSolutionsOpen && (
                <div className="flex flex-col gap-2 pl-2 mt-2 text-xs sm:text-sm">
                  <a href="/coaching" className="hover:text-white/70 transition-colors">Coaching</a>
                  <a href="/schools" className="hover:text-white/70 transition-colors">Schools</a>
                  <a href="/universities" className="hover:text-white/70 transition-colors">Universities</a>
                </div>
              )}
            </div>

            <div className="border-t border-white/10 pt-3 sm:pt-4">
              <button
                onClick={() => setIsCompanyOpen(!isCompanyOpen)}
                className="flex items-center justify-between w-full text-xs sm:text-sm font-bold uppercase tracking-wider text-white mb-2"
              >
                Company
                <span>{isCompanyOpen ? '▴' : '▾'}</span>
              </button>
              {isCompanyOpen && (
                <div className="flex flex-col gap-2 pl-2 mt-2 text-xs sm:text-sm">
                  <a href="/company/about" className="hover:text-white/70 transition-colors">About</a>
                  <a href="/company/careers" className="hover:text-white/70 transition-colors">Careers</a>
                  <a href="/company/trust" className="hover:text-white/70 transition-colors">Trust Center</a>
                </div>
              )}
            </div>

            <a href="/resources" className="text-base sm:text-lg font-bold hover:text-white/70 transition-colors block">Resources</a>
            <a href="/support" className="text-base sm:text-lg font-bold hover:text-white/70 transition-colors block">Support</a>

            <div className="border-t border-white/10 pt-3 sm:pt-4">
              <button
                onClick={handleGetStarted}
                className="w-full flex items-center justify-center gap-2 bg-white text-black font-bold px-4 py-2 rounded-lg hover:bg-gray-100 transition-colors text-sm"
              >
                Try Now <ArrowRight className="w-3 h-3" />
              </button>
            </div>
          </div>
        </div>
      )}
    </nav>
  );
};

const CaseStudySection = () => {
  return (
    <section className="py-12 md:py-24 bg-white relative overflow-hidden">
      <div className="max-w-7xl mx-auto px-6">
        <div className="text-center mb-8 md:mb-16">
          <span className="text-sm font-bold text-slate-900 uppercase tracking-widest bg-gray-100 px-4 py-1.5 rounded-full">Success Story</span>
          <h2 className="text-4xl sm:text-5xl font-bold text-black mt-4">Caught What Teachers Missed</h2>
          <p className="text-gray-600 max-w-2xl mx-auto mt-2 font-medium">Real-world impact of AI in answer sheet evaluation.</p>
        </div>

        <div className="bg-gradient-to-br from-gray-50 to-white p-6 sm:p-8 md:p-12 rounded-2xl sm:rounded-3xl border border-gray-100 shadow-sm max-w-4xl mx-auto relative">
          {/* Decorative quote mark */}
          <div className="absolute top-6 left-6 text-gray-200 text-6xl font-serif">“</div>
          
          <div className="relative z-10">
            <h3 className="text-2xl font-bold text-slate-900 mb-4">Anonymized Case Study</h3>
            <p className="text-slate-600 leading-relaxed mb-6">
              In a recent deployment at a large coaching institute, Parakh AI was used to re-evaluate 5,000 answer sheets that had already been manually checked by teachers. The AI flagged 150 instances where marks were calculated incorrectly, and 50 instances where entire answers were missed by the human evaluators. This ensured fair marking and saved the institute from potential disputes.
            </p>
            
            {/* Two-line summary */}
            <div className="border-l-4 border-black pl-4 mb-6">
              <p className="text-slate-800 font-medium text-lg">
                Parakh AI identified over 200 grading errors in a batch of 5,000 sheets previously marked by teachers.
              </p>
            </div>

            <a href="/resources/case-study" className="inline-flex items-center gap-2 font-bold text-black hover:text-gray-700 transition-colors">
              Read full case study 
              <ArrowRight className="w-4 h-4" />
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};

const TestimonialsSection = () => {
  const testimonials = [
    {
      name: "Amit Sharma",
      role: "Director",
      institution: "Apex Coachings",
      type: "Coaching",
      quote: "Parakh AI reduced our test evaluation time from 5 days to just 2 hours. Our students get results the same day!",
      image: "https://cdn.prod.website-files.com/693d418ddb735f60e5fb14f4/693e63eaa3099a015e509669_Frame%202121455028%20(1).png"
    },
    {
      name: "Dr. Sunil Rao",
      role: "Principal",
      institution: "Greenwood High",
      type: "School",
      quote: "The handwriting recognition is magical. It reads even the most cursive writing and grades fairly according to our rubric.",
      image: "https://cdn.prod.website-files.com/693d418ddb735f60e5fb14f4/693e63e9dde945ac836c668c_Frame%202121455030%20(1).png"
    },
    {
      name: "Prof. R.K. Verma",
      role: "HOD, Computer Science",
      institution: "State University",
      type: "University",
      quote: "Scaling semester evaluations for 10,000+ students was a nightmare. Parakh handled it flawlessly with complete audit trails.",
      image: "https://cdn.prod.website-files.com/693d418ddb735f60e5fb14f4/693e63e920afb5d98c9f9fc4_Frame%202121455037.png"
    }
  ];

  return (
    <section className="py-12 md:py-24 bg-white relative overflow-hidden">
      <div className="max-w-7xl mx-auto px-6">
        <div className="bg-[#f9f9f9] rounded-[24px] sm:rounded-[40px] p-6 sm:p-8 md:p-16">
          <div className="text-center mb-8 md:mb-16">
            <span className="text-sm font-bold text-slate-900 uppercase tracking-widest bg-gray-100 px-4 py-1.5 rounded-full">Testimonials</span>
            <h2 className="text-4xl sm:text-5xl font-bold text-black mt-4">Real Results from Real Educators</h2>
            <p className="text-gray-600 max-w-2xl mx-auto mt-2 font-medium">See how Parakh AI is transforming evaluation across India.</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {testimonials.map((test, index) => (
              <div 
                key={index} 
                className="bg-white rounded-2xl border border-gray-100 p-6 shadow-sm hover:shadow-md transition-shadow duration-300 flex flex-col"
              >
                {/* Header: Profile */}
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-full overflow-hidden">
                      <img src={test.image} alt={test.name} className="w-full h-full object-cover" />
                    </div>
                    <div>
                      <div className="flex items-center gap-1">
                        <h4 className="font-bold text-slate-900 text-sm">{test.name}</h4>
                        {/* Verified Badge */}
                        <svg className="w-4 h-4 text-blue-500" fill="currentColor" viewBox="0 0 20 20">
                          <path d="M6.267 3.455a3.066 3.066 0 001.745-.798 3.066 3.066 0 013.976 0 3.066 3.066 0 001.745.798 3.066 3.066 0 012.812 2.812 3.066 3.066 0 00.798 1.745 3.066 3.066 0 010 3.976 3.066 3.066 0 00-.798 1.745 3.066 3.066 0 01-2.812 2.812 3.066 3.066 0 00-1.745.798 3.066 3.066 0 01-3.976 0 3.066 3.066 0 00-1.745-.798 3.066 3.066 0 01-2.812-2.812 3.066 3.066 0 00-.798-1.745 3.066 3.066 0 010-3.976 3.066 3.066 0 00.798-1.745 3.066 3.066 0 012.812-2.812zm7.44 5.252a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" />
                        </svg>
                      </div>
                      <p className="text-xs text-slate-500">@{test.name.toLowerCase().replace(' ', '_')}</p>
                    </div>
                  </div>
                  {/* Twitter Logo */}
                  <svg className="w-4 h-4 text-[#1DA1F2]" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M23.953 4.57a10 10 0 01-2.825.775 4.958 4.958 0 002.163-2.723c-.951.555-2.005.959-3.127 1.184a4.92 4.92 0 00-8.384 4.482C7.69 8.095 4.067 6.13 1.64 3.162a4.822 4.822 0 00-.666 2.475c0 1.71.87 3.213 2.188 4.096a4.904 4.904 0 01-2.228-.616v.06a4.923 4.923 0 003.946 4.827 4.996 4.996 0 01-2.212.085 4.936 4.936 0 004.604 3.417 9.867 9.867 0 01-6.102 2.105c-.39 0-.779-.023-1.17-.067a13.995 13.995 0 007.557 2.209c9.053 0 13.998-7.496 13.998-13.985 0-.21 0-.42-.015-.63A9.935 9.935 0 0024 4.59z" />
                  </svg>
                </div>

                {/* Body: Quote */}
                <p className="text-slate-700 text-sm leading-relaxed mb-4 flex-grow">"{test.quote}"</p>

                {/* Footer: Date & Meta */}
                <div className="text-xs text-slate-400 mb-3">
                  9:24 AM · May 12, 2026
                </div>

                <div className="w-full h-px bg-gray-100 mb-3" />

                <div className="flex items-center gap-4 text-xs text-slate-500">
                  <span><strong>1.2k</strong> Likes</span>
                  <span><strong>340</strong> Retweets</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

const FinalCTASection = () => {
  return (
    <section className="py-12 md:py-24 bg-black text-white relative overflow-hidden">
      {/* Background Grid Pattern (Subtle) */}
      <div className="absolute inset-0 opacity-10 bg-[linear-gradient(to_right,#444_1px,transparent_1px),linear-gradient(to_bottom,#444_1px,transparent_1px)] bg-[size:40px_40px]" />
      
      {/* Glowing Gradient */}
      <div className="absolute -top-40 -left-40 w-80 h-80 bg-blue-500 rounded-full blur-[100px] opacity-20" />
      <div className="absolute -bottom-40 -right-40 w-80 h-80 bg-white rounded-full blur-[100px] opacity-10" />

      <div className="max-w-4xl mx-auto px-6 text-center relative z-10">
        <span className="text-sm font-bold uppercase tracking-widest text-gray-400 bg-white/10 px-4 py-1.5 rounded-full">Get Started</span>
        
        <h2 className="text-5xl sm:text-6xl font-bold mt-6 mb-4 bg-gradient-to-r from-white to-gray-400 bg-clip-text text-transparent">
          Stop marking. Start teaching.
        </h2>
        
        <p className="text-xl text-gray-400 mb-10 max-w-2xl mx-auto">
          Try Parakh free for 5 papers. No credit card required.
        </p>

        <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
          <a href="/support" className="w-full sm:w-auto bg-white text-black font-bold px-8 py-4 rounded-xl hover:bg-gray-100 transition-colors flex items-center justify-center gap-2 group">
            Start Free Trial
            <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
          </a>
          <a href="/support" className="w-full sm:w-auto bg-transparent border border-white/20 text-white font-bold px-8 py-4 rounded-xl hover:bg-white/5 transition-colors">
            Book a Demo
          </a>
        </div>

        <p className="text-xs text-gray-500 mt-6">
          Trusted by 100+ institutions across India.
        </p>
      </div>
    </section>
  );
};

export const Footer = () => {
  return (
    <footer className="bg-white py-8 sm:py-12 md:py-16 text-slate-600 font-sans relative">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <div className="bg-[#f9f9f9] rounded-2xl sm:rounded-[24px] md:rounded-[40px] p-4 sm:p-6 md:p-8 lg:p-12 relative">

          {/* Top Section */}
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 sm:gap-8 md:gap-12 mb-8 sm:mb-12">

            {/* Brand & Badges */}
            <div className="lg:col-span-4">
              <div className="flex items-center gap-2 sm:gap-3 mb-3 sm:mb-4">
                <div className="p-1 sm:p-1.5 rounded-lg sm:rounded-2xl bg-white border border-gray-100 shadow-[0_4px_12px_rgba(0,0,0,0.03)]">
                  <img
                    src="/new/my_wesbite_name_is_Parakh_202605191852.jpeg"
                    alt="Parakh AI Logo"
                    className="w-6 sm:w-9 h-6 sm:h-9 rounded-lg sm:rounded-xl object-contain"
                  />
                </div>
                <span className="font-sans font-black tracking-[-0.03em] text-base sm:text-xl transition-all duration-300">
                  <span className="text-slate-900">Parakh</span>
                  <span className="text-slate-500 ml-1">AI</span>
                </span>
              </div>
              <p className="text-xs sm:text-sm text-slate-600 mb-4 sm:mb-6 leading-relaxed">
                Automated evaluation system for handwritten answer sheets with high accuracy. Built for schools, universities, and coaching institutes.
              </p>

              {/* Badges Container like Screenshot */}
              <div className="bg-white border border-gray-100 rounded-xl sm:rounded-[24px] p-3 sm:p-6 max-w-lg mt-4 sm:mt-6">
                <div className="grid grid-cols-3 gap-3 sm:gap-6 items-center">
                  <img src="/ISO_27001_Final-Logo-removebg-preview.png" alt="ISO 27001" className="h-12 sm:h-20 object-contain mx-auto" />
                  <img src="/gdpr-compliant-removebg-preview.png" alt="GDPR Compliant" className="h-12 sm:h-20 object-contain mx-auto" />
                  <img src="/soc2-t2-compliance-badge-removebg-preview.png" alt="SOC2 Type 2" className="h-12 sm:h-20 object-contain mx-auto" />
                </div>
              </div>
            </div>

            {/* Links Grid */}
            <div className="lg:col-span-8 grid grid-cols-2 sm:grid-cols-3 gap-4 sm:gap-8">
              {/* SITEMAP */}
              <div>
                <h4 className="text-[10px] sm:text-xs font-bold text-slate-900 uppercase tracking-wider mb-2 sm:mb-4">Sitemap</h4>
                <ul className="space-y-2 sm:space-y-3 text-[10px] sm:text-sm">
                  <li><a href="/" className="hover:text-blue-600 transition-colors">Home</a></li>
                  <li><a href="/#how-it-works" className="hover:text-blue-600 transition-colors">Features</a></li>
                  <li><a href="/company/trust" className="hover:text-blue-600 transition-colors">Trust</a></li>
                  <li><a href="/company/about" className="hover:text-blue-600 transition-colors">Company</a></li>
                  <li><a href="/resources" className="hover:text-blue-600 transition-colors">Resources</a></li>
                </ul>
              </div>

              {/* CONTACT */}
              <div>
                <h4 className="text-[10px] sm:text-xs font-bold text-slate-900 uppercase tracking-wider mb-2 sm:mb-4">Contact</h4>
                <ul className="space-y-2 sm:space-y-3 text-[10px] sm:text-sm">
                  <li><a href="/support" className="hover:text-blue-600 transition-colors">Contact Us</a></li>
                  <li><a href="/support" className="hover:text-blue-600 transition-colors">Support</a></li>
                  <li><a href="mailto:info@kuberya.com" className="hover:text-blue-600 transition-colors break-all">info@kuberya</a></li>
                  <li><a href="mailto:hr@kuberya.com" className="hover:text-blue-600 transition-colors break-all">hr@kuberya</a></li>
                  <li><a href="https://wa.me/918989640004" className="hover:text-blue-600 transition-colors text-[9px] sm:text-sm">WhatsApp</a></li>
                </ul>
              </div>

              {/* LEGAL */}
              <div>
                <h4 className="text-[10px] sm:text-xs font-bold text-slate-900 uppercase tracking-wider mb-2 sm:mb-4">Legal</h4>
                <ul className="space-y-2 sm:space-y-3 text-[10px] sm:text-sm">
                  <li><a href="/company/trust" className="hover:text-blue-600 transition-colors">Privacy</a></li>
                  <li><a href="/company/trust" className="hover:text-blue-600 transition-colors">Terms</a></li>
                  <li><a href="/support" className="hover:text-blue-600 transition-colors">Grievance</a></li>
                </ul>
              </div>

            </div>
          </div>

          {/* Footer Bottom */}
          <div className="border-t border-gray-100 pt-6 sm:pt-8 flex flex-col sm:flex-row justify-between items-center gap-3 sm:gap-4">
            <p className="text-[9px] sm:text-xs text-slate-500 text-center sm:text-left">© 2026 KUBERYA AI PRIVATE LIMITED. ALL RIGHTS RESERVED.</p>
            <div className="flex items-center gap-2 sm:gap-4">
              <a href="https://www.linkedin.com/company/kuberya-ai/" target="_blank" rel="noopener noreferrer" className="w-6 sm:w-8 h-6 sm:h-8 rounded-full bg-white flex items-center justify-center text-slate-600 hover:bg-black hover:text-white transition-all border border-gray-100"><Linkedin className="w-3 sm:w-4 h-3 sm:h-4" /></a>
              <a href="https://www.instagram.com/kuberyaai/" target="_blank" rel="noopener noreferrer" className="w-6 sm:w-8 h-6 sm:h-8 rounded-full bg-white flex items-center justify-center text-slate-600 hover:bg-pink-600 hover:text-white transition-all border border-gray-100"><Instagram className="w-3 sm:w-4 h-3 sm:h-4" /></a>
              <a href="https://twitter.com/kuberyaai" target="_blank" rel="noopener noreferrer" className="w-6 sm:w-8 h-6 sm:h-8 rounded-full bg-gray-100 flex items-center justify-center text-slate-600 hover:bg-black hover:text-white transition-all"><Twitter className="w-3 sm:w-4 h-3 sm:h-4" /></a>
              <a href="https://www.facebook.com/profile.php?id=61575507550246" target="_blank" rel="noopener noreferrer" className="w-6 sm:w-8 h-6 sm:h-8 rounded-full bg-white flex items-center justify-center text-slate-600 hover:bg-blue-800 hover:text-white transition-all border border-gray-100"><Facebook className="w-3 sm:w-4 h-3 sm:h-4" /></a>
              <a href="https://www.youtube.com/@kuberya-AI" target="_blank" rel="noopener noreferrer" className="w-6 sm:w-8 h-6 sm:h-8 rounded-full bg-white flex items-center justify-center text-slate-600 hover:bg-red-600 hover:text-white transition-all border border-gray-100"><Youtube className="w-3 sm:w-4 h-3 sm:h-4" /></a>
            </div>
          </div>


        </div>
      </div>
    </footer>
  );
};

const LandingPage = () => {
  useSEO(seoConfig.home);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    setIsMobile(window.innerWidth < 768);
  }, []);

  const ScrollWrapper = ({ children }: { children: any }) => {
    return isMobile ? <>{children}</> : <ReactLenis root>{children}</ReactLenis>;
  };

  return (
    <>
      <Navbar />
      <ScrollWrapper>
        <div className="landing-page min-h-screen bg-white text-gray-900 selection:bg-gray-200 font-sans">
          <WanderfulHero />
          <ScrollingCardsSection />
          <PartnersSection />
          <StatsStripSection />
          <ProblemSection />
          <ApproachSection />
          <TestimonialsSection />
          <SolutionsSection />
          <FeaturesDeepDiveSection />
          <ComparisonSection />
          <CaseStudySection />
          <FinalCTASection />
          <Footer />
        </div>
      </ScrollWrapper>
    </>
  );
};

export default LandingPage;
